import Fuse from 'fuse.js'
import type { DayObject, DayObjectSearchEntry } from 'types/graphql'

import { logger as loggerDev } from 'src/lib/logger'
import {
  buildObjectReference,
  deepMerge,
  NativeObjectTypes,
  type NativeObjectType,
} from 'src/lib/objects'
import { searchMetadataBuilders } from 'src/lib/Objects/build'

const DB_NAME = 'day-objects-titanium'
const DB_VERSION = 1
const STORES = {
  OBJECTS: 'objects',
  FUSE_INDEX: 'fuseIndex',
  SYNC_QUEUE: 'syncQueue',
  DELETED_OBJECTS: 'deletedObjects',
  OBJECT_TYPE_METADATA: 'objectTypeMetadata',
} as const

const loggingEnabled = !process.env.HOST.includes('localhost')
const DEFAULT_PROCESSED_UNTIL_TIMESTAMP = '1970-01-01T00:00:00.000Z'

const logger = loggingEnabled
  ? loggerDev
  : {
      dev: () => {},
      info: () => {},
      warn: () => {},
      error: () => {},
    }

export const initDb = async (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      logger.warn('Error opening IndexedDB', {
        errorName: request.error?.name,
        errorMessage: request.error?.message,
      })
      reject(request.error)
    }

    request.onblocked = (event) => {
      logger.warn('IndexedDB blocked', {
        eventType: event.type,
        oldVersion: event.oldVersion,
        newVersion: event.newVersion,
      })
    }

    request.onsuccess = () => {
      resolve(request.result)
    }

    request.onupgradeneeded = (event) => {
      logger.info('Upgrading IndexedDB')
      const db = (event.target as IDBOpenDBRequest).result

      // Create stores with compound indexes
      if (!db.objectStoreNames.contains(STORES.OBJECTS)) {
        const objectsStore = db.createObjectStore(STORES.OBJECTS, {
          keyPath: 'key',
        })
        objectsStore.createIndex('byWorkspace', 'workspaceId')
        objectsStore.createIndex('byWorkspaceAndType', [
          'workspaceId',
          'objectType',
        ])
        objectsStore.createIndex('lastUpdated', 'lastUpdated')
      }

      if (!db.objectStoreNames.contains(STORES.FUSE_INDEX)) {
        const fuseIndexStore = db.createObjectStore(STORES.FUSE_INDEX, {
          keyPath: 'workspaceId',
        })
        fuseIndexStore.createIndex('byWorkspace', 'workspaceId')
      }
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncQueueStore = db.createObjectStore(STORES.SYNC_QUEUE, {
          keyPath: 'key',
        })
        syncQueueStore.createIndex('byWorkspaceAndObjectType', [
          'workspaceId',
          'objectType',
        ])
      }
      if (!db.objectStoreNames.contains(STORES.DELETED_OBJECTS)) {
        const deletedStore = db.createObjectStore(STORES.DELETED_OBJECTS, {
          keyPath: 'key',
        })
        deletedStore.createIndex('byWorkspace', 'workspaceId')
        logger.info(`Created ${STORES.DELETED_OBJECTS} store`)
      }
      if (!db.objectStoreNames.contains(STORES.OBJECT_TYPE_METADATA)) {
        const metadataStore = db.createObjectStore(
          STORES.OBJECT_TYPE_METADATA,
          {
            keyPath: 'key',
          }
        )
        metadataStore.createIndex('byWorkspace', 'workspaceId')
        logger.info(`Created ${STORES.OBJECT_TYPE_METADATA} store`)
      }
    }
  })
}

const buildSyncQueueKey = ({
  objectType,
  objectId,
  workspaceId,
}: {
  objectType: NativeObjectType
  objectId: string
  workspaceId: string
}) => `${objectType}-${objectId}-${workspaceId}`

export const addToSyncQueue = async ({
  objectType,
  workspaceId,
  items,
}: {
  objectType: NativeObjectType
  workspaceId: string
  items: { objectId: string; lastSyncedAt: string | null }[]
}) => {
  let db: IDBDatabase | null = null
  if (items?.length === 0) {
    logger.dev('addToSyncQueue: No items to add', {
      objectType,
      workspaceId,
    })
    return
  }
  logger.dev('addToSyncQueue: Attempting to add items', {
    objectType,
    count: items.length,
    sample: items.slice(0, 5).map((item) => item.objectId),
  })
  try {
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.SYNC_QUEUE, 'readwrite')
    const store = tx.objectStore(STORES.SYNC_QUEUE)

    const processedKeys = new Set<string>()

    const putPromises = items.map(async (item) => {
      const { objectId, lastSyncedAt } = item
      const key = buildSyncQueueKey({ objectType, objectId, workspaceId })
      if (processedKeys.has(key)) {
        logger.dev('addToSyncQueue: Entry already processed, skipping', { key })
        return
      }
      processedKeys.add(key)

      return new Promise((resolve, reject) => {
        const request = store.get(key)

        request.onsuccess = () => {
          if (!request.result) {
            const syncEntry = {
              key,
              objectType,
              workspaceId,
              objectId,
              addedAt: new Date().toISOString(),
              lastSyncedAt,
            }
            logger.dev('addToSyncQueue: Adding entry', { key, lastSyncedAt })
            const addRequest = store.add(syncEntry)
            addRequest.onsuccess = () => {
              logger.dev('addToSyncQueue: Successfully added', { key })
              resolve(undefined)
            }
            addRequest.onerror = () => {
              logger.warn('addToSyncQueue: Failed to add entry', {
                key,
                errorName: addRequest.error?.name,
                errorMessage: addRequest.error?.message,
              })
              reject(addRequest.error)
            }
          } else {
            logger.dev('addToSyncQueue: Entry already exists, skipping', {
              key,
            })
            resolve(undefined)
          }
        }
        request.onerror = () => {
          logger.warn(
            'addToSyncQueue: Failed to get entry for existence check',
            {
              key,
              errorName: request.error?.name,
              errorMessage: request.error?.message,
            }
          )
          reject(request.error)
        }
      })
    })

    await Promise.all(putPromises)
    logger.dev('addToSyncQueue: Finished processing batch', {
      objectType,
      count: items.length,
    })
  } catch (error) {
    logger.warn('addToSyncQueue: Error during transaction', {
      objectType,
      error,
    })
  } finally {
    //if (db) db.close()
  }
}

export const getSyncQueueEntries = async ({
  workspaceId,
}: {
  workspaceId: string
}) => {
  let db: IDBDatabase | null = null
  let finalResults: any[] = []
  try {
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.SYNC_QUEUE, 'readonly')
    const store = tx.objectStore(STORES.SYNC_QUEUE)

    type SyncQueueEntry = {
      key: string
      workspaceId: string
      objectType: NativeObjectType
      objectId: string
      addedAt: string
    }

    const results: SyncQueueEntry[] = await new Promise((resolve, reject) => {
      const request = store.getAll()

      request.onsuccess = () => {
        const allEntries = (request.result as SyncQueueEntry[]) || []
        const filteredEntries = allEntries.filter((entry) => {
          return entry.workspaceId === workspaceId
        })
        resolve(filteredEntries)
      }
      request.onerror = () => reject(request.error)
    })

    const sortedResults = results.sort((a, b) => {
      return new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime()
    })

    finalResults = sortedResults
  } catch (error) {
    logger.warn('Error getting sync queue entries', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    })
    finalResults = []
  } finally {
    if (db) db.close()
  }
  return finalResults
}

export const removeFromSyncQueue = async ({
  objectType,
  objectIds,
  workspaceId,
}: {
  objectType: NativeObjectType
  objectIds: string[]
  workspaceId: string
}) => {
  logger.dev('removeFromSyncQueue: Starting', {
    objectType,
    count: objectIds.length,
    workspaceId,
    sample: objectIds.slice(0, 5),
  })
  let db: IDBDatabase | null = null
  let deletedCount = 0
  const errors: { key: string; error: any }[] = []

  try {
    logger.dev('removeFromSyncQueue: Initializing DB')
    db = await initDb()
    logger.dev('removeFromSyncQueue: Starting transaction')
    const tx = (db as IDBDatabase).transaction(STORES.SYNC_QUEUE, 'readwrite')
    const store = tx.objectStore(STORES.SYNC_QUEUE)

    const deletePromises = objectIds.map((objectId) => {
      const key = buildSyncQueueKey({ objectType, objectId, workspaceId })
      return new Promise<void>((resolve, _reject) => {
        logger.dev('removeFromSyncQueue: Attempting delete for key', { key })
        const deleteRequest = store.delete(key)

        deleteRequest.onsuccess = () => {
          logger.dev('removeFromSyncQueue: Successfully deleted key', { key })
          deletedCount++
          resolve()
        }

        deleteRequest.onerror = () => {
          logger.warn(`removeFromSyncQueue: Failed to delete key ${key}`, {
            errorName: deleteRequest.error?.name,
            errorMessage: deleteRequest.error?.message,
          })
          errors.push({ key, error: deleteRequest.error })
          resolve()
        }
      })
    })

    await Promise.all(deletePromises)

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => {
        logger.dev('removeFromSyncQueue: Transaction completed', {
          deletedCount,
          errorCount: errors.length,
        })
        resolve()
      }
      tx.onerror = () => {
        logger.warn(`removeFromSyncQueue: Transaction error`, {
          errorName: tx.error?.name,
          errorMessage: tx.error?.message,
        })
        reject(tx.error)
      }
      tx.onabort = () => {
        logger.warn(`removeFromSyncQueue: Transaction aborted`, {
          errorName: tx.error?.name,
          errorMessage: tx.error?.message,
        })
        reject(tx.error)
      }
    })
  } catch (error) {
    logger.warn(
      `removeFromSyncQueue: Error during DB init or transaction setup`,
      {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      }
    )
  } finally {
    if (db) db.close()
  }

  logger.dev('removeFromSyncQueue: Finished', {
    deletedCount,
    errorCount: errors.length,
    errorsSample: errors.slice(0, 5),
  })
}

export const getDayObjectState = async ({
  workspaceId,
}: {
  workspaceId: string
}) => {
  let db: IDBDatabase | null = null
  let stateByObjectType: Record<NativeObjectType, any> | null = null
  logger.dev('[getDayObjectState] Called', { workspaceId })

  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.OBJECTS, 'readonly')
    const store = tx.objectStore(STORES.OBJECTS)

    let results: DayObjectSearchEntry[] = []
    results = await new Promise<DayObjectSearchEntry[]>((resolve, reject) => {
      const request = store.index('byWorkspace').getAll(workspaceId)
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
    logger.dev('[getDayObjectState] Fetched main object results', {
      count: results.length,
    })

    // Fetch processedUntilTimestamps for all relevant types in this workspace
    const metadataTx = (db as IDBDatabase).transaction(
      STORES.OBJECT_TYPE_METADATA,
      'readonly'
    )
    const metadataStore = metadataTx.objectStore(STORES.OBJECT_TYPE_METADATA)
    const metadataRequest = metadataStore
      .index('byWorkspace')
      .getAll(workspaceId)

    const metadataResults: Array<{
      key: string
      workspaceId: string
      objectType: NativeObjectType
      processedUntilTimestamp: string
    }> = await new Promise((resolve, reject) => {
      metadataRequest.onsuccess = () => resolve(metadataRequest.result || [])
      metadataRequest.onerror = () => reject(metadataRequest.error)
    })
    logger.dev('[getDayObjectState] Fetched metadataResults', {
      count: metadataResults.length,
    })

    const processedTimestampsMap = new Map<NativeObjectType, string>()
    metadataResults.forEach((meta) => {
      processedTimestampsMap.set(meta.objectType, meta.processedUntilTimestamp)
    })

    stateByObjectType = results.reduce(
      (acc, obj) => {
        const objectType = obj.objectType as NativeObjectType
        const currentState = acc[objectType] || {
          currentCount: 0,
          updatedAt: null,
          objectsNeedingSync: 0,
          syncedAt: null,
          processedUntilTimestamp:
            processedTimestampsMap.get(objectType) ||
            DEFAULT_PROCESSED_UNTIL_TIMESTAMP,
        }
        const updatedAt = new Date(obj.updatedAt)

        let maxSyncedAt = currentState.syncedAt
        if (obj.syncedAt) {
          const currentObjSyncedAt = new Date(obj.syncedAt)
          if (
            maxSyncedAt === null ||
            currentObjSyncedAt.getTime() > maxSyncedAt.getTime()
          ) {
            maxSyncedAt = currentObjSyncedAt
          }
        }

        acc[objectType] = {
          currentCount: currentState.currentCount + 1,
          updatedAt:
            currentState.updatedAt === null ||
            new Date(updatedAt).getTime() >
              new Date(currentState.updatedAt).getTime()
              ? updatedAt
              : currentState.updatedAt,
          objectsNeedingSync:
            currentState.objectsNeedingSync + (obj.syncedAt ? 0 : 1),
          syncedAt: maxSyncedAt,
          processedUntilTimestamp: currentState.processedUntilTimestamp,
        }

        return acc
      },
      {} as Record<
        NativeObjectType,
        {
          currentCount: number
          updatedAt: Date | null
          objectsNeedingSync: number
          syncedAt: Date | null
          processedUntilTimestamp: string
        }
      >
    )

    logger.dev('[getDayObjectState] State after reduce', {
      stateByObjectType: stateByObjectType
        ? JSON.parse(JSON.stringify(stateByObjectType))
        : null,
    })

    // Ensure all object types (even those with no objects yet) have a state entry
    // including their processedUntilTimestamp.
    Object.values(NativeObjectTypes).forEach((typeValue) => {
      const objectType = typeValue as NativeObjectType
      // Ensure objectType is a string and a valid key for NativeObjectTypes
      if (typeof objectType === 'string' && NativeObjectTypes[objectType]) {
        if (!stateByObjectType[objectType]) {
          stateByObjectType[objectType] = {
            currentCount: 0,
            updatedAt: null,
            objectsNeedingSync: 0,
            syncedAt: null,
            processedUntilTimestamp:
              processedTimestampsMap.get(objectType) ||
              DEFAULT_PROCESSED_UNTIL_TIMESTAMP,
          }
        }
      }
    })

    logger.dev('[getDayObjectState] Final state before return', {
      stateByObjectType: stateByObjectType
        ? JSON.parse(JSON.stringify(stateByObjectType))
        : null,
    })
  } catch (error) {
    logger.info('Error getting day object state', {
      errorName: (error as Error)?.name,
      errorMessage: (error as Error)?.message,
      stack: (error as Error)?.stack,
    })
    // If an error occurs, stateByObjectType remains null (its initial value)
  } finally {
    if (db) db.close()
  }
  return stateByObjectType
}

export const getDayObjectStateByType = async ({
  workspaceId,
  objectType,
}: {
  workspaceId: string
  objectType: NativeObjectType
}) => {
  let db: IDBDatabase | null = null
  let stateByObjectType: Record<
    NativeObjectType,
    {
      currentCount: number
      updatedAt: Date | null
      objectsNeedingSync: number
      syncedAt: Date | null
      processedUntilTimestamp: string
    }
  > | null = null
  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.OBJECTS, 'readonly')
    const store = tx.objectStore(STORES.OBJECTS)

    const results: DayObjectSearchEntry[] = await new Promise(
      (resolve, reject) => {
        const range = IDBKeyRange.only([workspaceId, objectType])
        const request = store.index('byWorkspaceAndType').getAll(range)
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      }
    )

    // Fetch processedUntilTimestamp for this specific type
    let processedTimestampForType = DEFAULT_PROCESSED_UNTIL_TIMESTAMP
    try {
      const metadataTx = (db as IDBDatabase).transaction(
        STORES.OBJECT_TYPE_METADATA,
        'readonly'
      )
      const metadataStore = metadataTx.objectStore(STORES.OBJECT_TYPE_METADATA)
      const metadataKey = `${workspaceId}-${objectType}`
      const metadataRequest = metadataStore.get(metadataKey)

      const metadataEntry: {
        processedUntilTimestamp?: string
      } | null = await new Promise((resolve, reject) => {
        metadataRequest.onsuccess = () => resolve(metadataRequest.result)
        metadataRequest.onerror = () => reject(metadataRequest.error)
      })

      if (metadataEntry && metadataEntry.processedUntilTimestamp) {
        processedTimestampForType = metadataEntry.processedUntilTimestamp
      }
    } catch (metaError) {
      logger.warn('Failed to get processedUntilTimestamp for type', {
        objectType,
        errorName: (metaError as Error)?.name,
        errorMessage: (metaError as Error)?.message,
      })
    }

    stateByObjectType = results.reduce(
      (acc, obj) => {
        const currentObjType = obj.objectType as NativeObjectType
        const currentState = acc[currentObjType] || {
          currentCount: 0,
          updatedAt: null,
          objectsNeedingSync: 0,
          syncedAt: null,
          processedUntilTimestamp: processedTimestampForType,
        }
        const updatedAt = new Date(obj.updatedAt)

        let maxSyncedAt = currentState.syncedAt
        if (obj.syncedAt) {
          const currentObjSyncedAt = new Date(obj.syncedAt)
          if (
            maxSyncedAt === null ||
            currentObjSyncedAt.getTime() > maxSyncedAt.getTime()
          ) {
            maxSyncedAt = currentObjSyncedAt
          }
        }

        acc[currentObjType] = {
          currentCount: currentState.currentCount + 1,
          updatedAt:
            currentState.updatedAt === null ||
            updatedAt > currentState.updatedAt
              ? updatedAt
              : currentState.updatedAt,
          objectsNeedingSync:
            currentState.objectsNeedingSync + (obj.syncedAt ? 0 : 1),
          syncedAt: maxSyncedAt,
          processedUntilTimestamp: currentState.processedUntilTimestamp,
        }

        return acc
      },
      {} as Record<
        NativeObjectType,
        {
          currentCount: number
          updatedAt: Date | null
          objectsNeedingSync: number
          syncedAt: Date | null
          processedUntilTimestamp: string
        }
      >
    )
  } catch (error) {
    logger.warn('Error getting day object state by type', {
      workspaceId,
      objectType,
      error,
    })
  } finally {
    if (db) db.close()
  }
  return stateByObjectType
}

export const getNeverSyncedObjects = async ({
  workspaceId,
}: {
  workspaceId: string
}) => {
  let db: IDBDatabase | null = null
  let neverSyncedObjects: DayObjectSearchEntry[] | null = null
  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.OBJECTS, 'readonly')
    const store = tx.objectStore(STORES.OBJECTS)

    const [results] = await Promise.all([
      new Promise<DayObjectSearchEntry[]>((resolve, reject) => {
        const request = store.index('byWorkspace').getAll(workspaceId)
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      }),
    ])

    neverSyncedObjects = results.filter(
      (obj) => !obj.syncedAt || !obj?.object?.updatedAt
    )
  } catch (error) {
    logger.warn('Error getting never synced objects', { error })
  } finally {
    if (db) db.close()
  }
  return neverSyncedObjects
}

export const upsertDayObjects = async ({
  objects,
  objectType,
  workspaceId,
  synced = false,
}: {
  objects: Partial<DayObject>[]
  objectType: NativeObjectType
  workspaceId: string
  synced?: boolean
}) => {
  if (!objects || !Array.isArray(objects)) {
    logger.warn('Invalid objects in upsertDayObjects', { objects })
    return
  }

  let db: IDBDatabase | null = null

  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.OBJECTS, 'readwrite')
    const store = tx.objectStore(STORES.OBJECTS)

    const existingObjects = await new Promise<any[]>((resolve, reject) => {
      const request = store.index('byWorkspace').getAll(workspaceId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })

    const existingObjectsMap = existingObjects
      ? new Map(existingObjects.map((obj) => [obj.key, obj]))
      : new Map()

    const putPromises = objects.map(async (obj) => {
      if (!obj || !obj.objectId) {
        logger.warn('Invalid object in upsertDayObjects', { obj })
        return Promise.resolve()
      }

      const key = buildObjectReference({
        workspaceId,
        objectId: obj.objectId,
        objectType,
      })

      const searchMetadata =
        synced && obj.properties ? searchMetadataBuilders[objectType](obj) : {}
      if (!searchMetadata) {
        logger.warn('Could not build search metadata for object', {
          objectId: obj.objectId,
          objectType,
        })
        return Promise.resolve()
      }

      const currentTime = new Date().toISOString()
      const updatedAt = obj.updatedAt ? obj.updatedAt : currentTime

      const newObject = {
        key,
        workspaceId,
        objectType,
        objectId: obj.objectId,
        updatedAt,

        syncedAt: synced ? currentTime : null,
        object: obj,
        ...searchMetadata,
      }

      const existingObject = existingObjectsMap.get(key)

      if (existingObject) {
        const mergedObject = deepMerge(existingObject, newObject)

        const existingUpdatedAt = existingObject.updatedAt
          ? new Date(existingObject.updatedAt).getTime()
          : 0
        const newUpdatedAt = updatedAt
          ? new Date(updatedAt).getTime()
          : new Date().getTime()

        const mostRecentUpdatedAt = Math.max(existingUpdatedAt, newUpdatedAt)
        mergedObject.updatedAt = new Date(mostRecentUpdatedAt).toISOString()

        return new Promise((resolve, reject) => {
          const request = store.put(mergedObject)
          request.onsuccess = () => resolve(undefined)
          request.onerror = () => {
            logger.warn('Error updating object in IndexedDB', {
              objectId: obj.objectId,
            })
            reject(request.error)
          }
        })
      } else {
        return new Promise((resolve, reject) => {
          const request = store.add(newObject)
          request.onsuccess = () => resolve(undefined)
          request.onerror = () => {
            logger.warn('Error adding new object to IndexedDB', {
              objectId: obj.objectId,
            })
            reject(request.error)
          }
        })
      }
    })

    await Promise.all(putPromises.filter(Boolean))
    if (synced) {
      await removeFromSyncQueue({
        objectType,
        objectIds: objects.map((obj) => obj.objectId),
        workspaceId,
      })
    }
  } finally {
    if (db) db.close()
  }
}

export const getDayObjectSearchEntry = async ({
  workspaceId,
  objectType,
  objectId,
}: {
  workspaceId: string
  objectType: NativeObjectType
  objectId: string
}): Promise<DayObjectSearchEntry | null> => {
  let db: IDBDatabase | null = null
  let result: DayObjectSearchEntry | null = null
  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(
      [STORES.OBJECTS, STORES.DELETED_OBJECTS],
      'readonly'
    )
    const store = tx.objectStore(STORES.OBJECTS)
    const deletedStore = tx.objectStore(STORES.DELETED_OBJECTS)

    const key = buildObjectReference({
      workspaceId,
      objectId,
      objectType,
    })

    const deletedCheck = await new Promise<boolean>((resolve, reject) => {
      const request = deletedStore.getKey(key)
      request.onsuccess = () => resolve(!!request.result)
      request.onerror = () => reject(request.error)
    })

    if (deletedCheck) {
      logger.dev('getDayObjectSearchEntry: Object is deleted', { key })
      return null
    }

    logger.dev('Getting object', { key })
    result = await new Promise<any>((resolve, reject) => {
      const request = store.get(key)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    logger.dev('Got object', { result: !!result })
  } catch (error) {
    logger.warn('Error getting day object search entry', { error })
  } finally {
    if (db) db.close()
  }
  return result
}

const _filteredResults = ({
  result,
  objectType,
  objectIds,
}: {
  result: DayObjectSearchEntry[]
  objectType: NativeObjectType
  objectIds: string[]
}): DayObjectSearchEntry[] => {
  return (result || []).filter((searchEntry) => {
    if (
      objectIds.includes(searchEntry.objectId) &&
      searchEntry.object?.objectType === objectType &&
      searchEntry.object?.objectId
    ) {
      return true
    } else {
      return false
    }
  })
}

export const getDayObjects = async ({
  workspaceId,
  objectType,
  objectIds,
}: {
  workspaceId: string
  objectType: NativeObjectType
  objectIds: string[]
}): Promise<DayObjectSearchEntry[]> => {
  let db: IDBDatabase | null = null
  let finalResult: DayObjectSearchEntry[] = []
  try {
    if (!objectIds || objectIds.length === 0) {
      logger.warn('No object ids provided to getDayObjects', {
        objectType,
        workspaceId,
      })
      return []
    }
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.OBJECTS, 'readonly')
    const store = tx.objectStore(STORES.OBJECTS)

    const [results] = await Promise.all([
      new Promise<DayObjectSearchEntry[]>((resolve, reject) => {
        const range = IDBKeyRange.only([workspaceId, objectType])
        const request = store.index('byWorkspaceAndType').getAll(range)
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      }),
    ])

    const relevantObjectIds = new Set(objectIds)

    finalResult = results.filter((entry) =>
      relevantObjectIds.has(entry.objectId)
    )

    logger.dev('getDayObjects result', { count: finalResult.length })
  } catch (error) {
    logger.warn('Error getting day objects', { error })
  } finally {
    if (db) db.close()
  }
  return finalResult
}

export const getDayObjectsByType = async ({
  workspaceId,
  objectType,
}: {
  workspaceId: string
  objectType: NativeObjectType
}): Promise<DayObjectSearchEntry[]> => {
  let db: IDBDatabase | null = null
  let result: DayObjectSearchEntry[] = []
  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.OBJECTS, 'readonly')
    const store = tx.objectStore(STORES.OBJECTS)

    const [results] = await Promise.all([
      new Promise<DayObjectSearchEntry[]>((resolve, reject) => {
        const range = IDBKeyRange.only([workspaceId, objectType])
        const request = store.index('byWorkspaceAndType').getAll(range)
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      }),
    ])

    result = results
  } catch (error) {
    logger.warn('Error getting day objects by type', { error })
  } finally {
    if (db) db.close()
  }
  return result
}

export const getDayObjectsRecentByTypes = async ({
  workspaceId,
  objectTypes,
}: {
  workspaceId: string
  objectTypes: NativeObjectType[]
}): Promise<Record<NativeObjectType, DayObjectSearchEntry[]>> => {
  let db: IDBDatabase | null = null
  const result: Record<NativeObjectType, DayObjectSearchEntry[]> = {} as any
  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.OBJECTS, 'readonly')
    const store = tx.objectStore(STORES.OBJECTS)

    const [allResults] = await Promise.all([
      new Promise<DayObjectSearchEntry[]>((resolve, reject) => {
        const request = store.index('byWorkspace').getAll(workspaceId)
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      }),
    ])

    const resultsByType: Record<string, DayObjectSearchEntry[]> = {}
    for (const obj of allResults) {
      if (objectTypes.includes(obj.objectType as NativeObjectType)) {
        if (!resultsByType[obj.objectType]) {
          resultsByType[obj.objectType] = []
        }
        resultsByType[obj.objectType].push(obj)
      }
    }

    for (const objectType of objectTypes) {
      const typeResults = resultsByType[objectType] || []
      const sortedResultsByType = typeResults.sort((a, b) => {
        const timeA = a?.object?.updatedAt
          ? new Date(a.object.updatedAt).getTime()
          : 0
        const timeB = b?.object?.updatedAt
          ? new Date(b.object.updatedAt).getTime()
          : 0
        return timeB - timeA
      })
      result[objectType as NativeObjectType] = sortedResultsByType.slice(0, 50)
    }
  } catch (error) {
    logger.warn('Error getting recent day objects by type', { error })
  } finally {
    if (db) db.close()
  }
  return result
}

export const deleteDayObjectSearchEntry = async (key: string) => {
  let db: IDBDatabase | null = null
  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(
      [STORES.OBJECTS, STORES.DELETED_OBJECTS],
      'readwrite'
    )
    const store = tx.objectStore(STORES.OBJECTS)
    const deletedStore = tx.objectStore(STORES.DELETED_OBJECTS)

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(key)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })

    await new Promise<void>((resolve, _reject) => {
      const request = deletedStore.delete(key)
      request.onsuccess = () => resolve()
      request.onerror = (e) => {
        logger.dev(
          'deleteDayObjectSearchEntry: Key not found in deleted store or error',
          { key, error: (e.target as IDBRequest).error }
        )
        resolve()
      }
    })
  } catch (error) {
    logger.warn('Error deleting day object search entry', { key, error })
    throw error
  } finally {
    if (db) db.close()
  }
}

export const searchOptions = {
  keys: [
    { name: 'label', weight: 2 },
    { name: 'searchText', weight: 1.5 },
  ],
  includeScore: true,
  threshold: 0.2,
  distance: 100,
  ignoreLocation: true,
  findAllMatches: true,
  useExtendedSearch: true,
  //minMatchCharLength: 4,
}

export const buildFuseIndex = async (workspaceId: string): Promise<void> => {
  let db: IDBDatabase | null = null

  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(
      [STORES.OBJECTS, STORES.FUSE_INDEX, STORES.DELETED_OBJECTS],
      'readwrite'
    )

    const objectStore = tx.objectStore(STORES.OBJECTS)
    const fuseIndexStore = tx.objectStore(STORES.FUSE_INDEX)
    const deletedStore = tx.objectStore(STORES.DELETED_OBJECTS)

    const [objects] = await Promise.all([
      new Promise<DayObjectSearchEntry[]>((resolve, reject) => {
        const request = objectStore.index('byWorkspace').getAll(workspaceId)
        request.onerror = () => reject(request.error)
        request.onsuccess = () => resolve(request.result || [])
      }),
      new Promise<Set<string>>((resolve, reject) => {
        const keys = new Set<string>()
        const cursorRequest = deletedStore
          .index('byWorkspace')
          .openKeyCursor(IDBKeyRange.only(workspaceId))
        cursorRequest.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursor>).result
          if (cursor) {
            keys.add(cursor.primaryKey as string)
            cursor.continue()
          } else {
            resolve(keys)
          }
        }
        cursorRequest.onerror = (event) =>
          reject((event.target as IDBRequest).error)
      }),
    ])

    if (!objects || objects.length === 0) {
      logger.warn('No objects found to build index for workspace', {
        workspaceId,
      })
      await new Promise<void>((resolve, reject) => {
        const deleteRequest = fuseIndexStore.delete(workspaceId)
        deleteRequest.onsuccess = () => resolve()
        deleteRequest.onerror = () => reject(deleteRequest.error)
      })
      return
    }

    if (objects.length === 0) {
      logger.warn('buildFuseIndex: All objects were deleted, clearing index', {
        workspaceId,
      })
      await new Promise<void>((resolve, reject) => {
        const deleteRequest = fuseIndexStore.delete(workspaceId)
        deleteRequest.onsuccess = () => resolve()
        deleteRequest.onerror = () => reject(deleteRequest.error)
      })
      return
    }

    const searchableObjects = objects.map((obj) => {
      const label =
        obj.label ||
        (obj.object as any)?.name ||
        (obj.object as any)?.title ||
        obj.objectId ||
        ''
      const description =
        obj.description || (obj.object as any)?.description || ''

      return {
        key: obj.key,
        label,
        description,
        objectType: obj.objectType,
        objectId: obj.objectId,
        workspaceId: obj.workspaceId,
        updatedAt: obj.updatedAt,
        searchText: obj.searchText || '',
      }
    })

    const indexKeys = searchOptions.keys.map((k) =>
      typeof k === 'string' ? k : k.name
    )

    const finalSearchableObjects = searchableObjects.filter((item) =>
      indexKeys.every((key) => item[key] !== undefined && item[key] !== null)
    )

    if (finalSearchableObjects.length < searchableObjects.length) {
      logger.warn(
        'buildFuseIndex: Some objects lacked required keys for indexing',
        {
          originalCount: searchableObjects.length,
          finalCount: finalSearchableObjects.length,
          keys: indexKeys,
        }
      )
    }

    if (finalSearchableObjects.length === 0) {
      logger.warn(
        'buildFuseIndex: No valid objects left after key check for indexing',
        { workspaceId }
      )
      return
    }

    const fuseIndex = Fuse.createIndex(indexKeys, finalSearchableObjects)

    const indexData = {
      workspaceId,
      index: fuseIndex.toJSON(),
      lastBuilt: new Date().toISOString(),
      objectCount: finalSearchableObjects.length,
    }

    await new Promise<void>((resolve, reject) => {
      const request = fuseIndexStore.put(indexData)
      request.onerror = () => {
        logger.error('Error saving Fuse index:', request.error)
        reject(request.error)
      }
      request.onsuccess = () => {
        logger.dev('Successfully built and saved fuse index', {
          objectCount: finalSearchableObjects.length,
        })
        resolve()
      }
    })
  } catch (error) {
    logger.error('Failed to build Fuse index:', error)
    throw error
  } finally {
    if (db) db.close()
  }
}

export const getFuseIndex = async (workspaceId: string) => {
  let db: IDBDatabase | null = null
  let result: any | null = null
  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.FUSE_INDEX, 'readonly')
    const store = tx.objectStore(STORES.FUSE_INDEX)

    result = await new Promise((resolve, reject) => {
      const request = store.get(workspaceId)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  } catch (error) {
    logger.warn('Error getting Fuse index', { error })
  } finally {
    if (db) db.close()
  }
  return result
}

export const searchWithFuseIndex = async ({
  workspaceId,
  query,
  objectTypes,
}: {
  workspaceId: string
  query?: string
  objectTypes?: NativeObjectType[]
}): Promise<DayObjectSearchEntry[]> => {
  let db: IDBDatabase | null = null
  let finalResults: DayObjectSearchEntry[] = []
  const startTimer = new Date().getTime()
  try {
    await initDb()
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.OBJECTS, 'readonly')

    logger.dev('searchWithFuseIndex: initDb', {
      timeElapsed: new Date().getTime() - startTimer,
    })

    if (!query?.trim()) {
      const resultsByType = await Promise.all(
        (objectTypes || Object.values(NativeObjectTypes)).map((objectType) =>
          getDayObjectsByType({ workspaceId, objectType })
        )
      )
      return resultsByType.flat()
    }

    const [objects] = await Promise.all([
      new Promise<DayObjectSearchEntry[]>((resolve, reject) => {
        const store = tx.objectStore(STORES.OBJECTS)
        const request = store.index('byWorkspace').getAll(workspaceId)
        request.onerror = () => reject(request.error)
        request.onsuccess = () => resolve(request.result || [])
      }),
    ])

    logger.dev('searchWithFuseIndex: get all objects', {
      timeElapsed: new Date().getTime() - startTimer,
    })

    const indexData = await getFuseIndex(workspaceId)

    logger.dev('searchWithFuseIndex: getFuseIndex', {
      timeElapsed: new Date().getTime() - startTimer,
    })

    if (!indexData || !objects?.length) {
      logger.warn('No Fuse index or objects found for workspace search', {
        workspaceId,
      })
      return []
    }

    const validObjects = objects
    if (validObjects.length === 0) {
      logger.dev(
        'searchWithFuseIndex: All objects were deleted, returning empty results.',
        { workspaceId }
      )
      return []
    }

    let objectsToSearch = validObjects
    if (objectTypes && objectTypes.length > 0) {
      objectsToSearch = validObjects.filter((obj) =>
        objectTypes.includes(obj.objectType as NativeObjectType)
      )
    }

    if (objectsToSearch.length === 0) {
      logger.dev(
        'searchWithFuseIndex: No objects to search after type filtering, returning empty results.',
        { workspaceId, objectTypes }
      )
      return []
    }

    const fuse = new Fuse(
      objectsToSearch,
      searchOptions,
      Fuse.parseIndex(indexData.index)
    )

    logger.dev('searchWithFuseIndex: fuse.search', {
      timeElapsed: new Date().getTime() - startTimer,
    })

    const results = fuse.search(query)

    logger.dev('searchWithFuseIndex: getDayObjectsByType', {
      timeElapsed: new Date().getTime() - startTimer,
    })

    finalResults = results
      .map((result) => result.item as DayObjectSearchEntry)
      .filter(
        (item) =>
          !objectTypes ||
          objectTypes.includes(item?.objectType as NativeObjectType)
      )
      .filter(Boolean)
  } catch (error) {
    logger.error('Search failed:', error)
    return []
  } finally {
    if (db) db.close()
  }
  return finalResults
}

export async function clearDayObjects(): Promise<void> {
  logger.dev('clearDayObjects: Attempting to delete database', { DB_NAME })
  try {
    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(DB_NAME)

      request.onerror = (event) => {
        logger.warn('clearDayObjects: Failed to delete database:', {
          errorName: request.error?.name,
          errorMessage: request.error?.message,
          eventType: event.type,
        })
        reject(request.error)
      }

      request.onsuccess = (event) => {
        logger.dev('clearDayObjects: Successfully deleted database', { event })
        resolve()
      }

      request.onblocked = (event) => {
        logger.warn('clearDayObjects: Delete database request blocked', {
          eventType: event.type,
          oldVersion: (event as IDBVersionChangeEvent).oldVersion,
          newVersion: (event as IDBVersionChangeEvent).newVersion,
        })
        reject(new Error('Database deletion blocked by open connections.'))
      }
    })
  } catch (error) {
    logger.warn('clearDayObjects: Error during deletion process', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    })
    throw error
  }
}

export const purgeOldSyncQueueEntries = async ({
  workspaceId,
  purgeBeforeDate,
}: {
  workspaceId: string
  purgeBeforeDate: Date
}) => {
  let db: IDBDatabase | null = null
  let deletedCount = 0
  logger.dev('purgeOldSyncQueueEntries: Starting purge', {
    purgeBeforeDate: purgeBeforeDate.toISOString(),
  })

  try {
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(STORES.SYNC_QUEUE, 'readwrite')
    const store = tx.objectStore(STORES.SYNC_QUEUE)

    await new Promise<void>((resolve, reject) => {
      const cursorRequest = store.openCursor()
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result
        if (cursor) {
          const entry = cursor.value
          if (
            entry.workspaceId === workspaceId &&
            new Date(entry.addedAt) < purgeBeforeDate
          ) {
            logger.dev('purgeOldSyncQueueEntries: Deleting old entry', {
              key: entry.key,
              addedAt: entry.addedAt,
            })
            const deleteRequest = cursor.delete()
            deleteRequest.onsuccess = () => {
              deletedCount++
            }
            deleteRequest.onerror = () => {
              logger.warn('purgeOldSyncQueueEntries: Failed to delete entry', {
                key: entry.key,
                errorName: deleteRequest.error?.name,
                errorMessage: deleteRequest.error?.message,
              })
            }
          }
          cursor.continue()
        } else {
          resolve()
        }
      }
      cursorRequest.onerror = (event) => {
        logger.warn('purgeOldSyncQueueEntries: Error opening cursor', {
          errorName: (event.target as IDBRequest).error?.name,
          errorMessage: (event.target as IDBRequest).error?.message,
        })
        reject((event.target as IDBRequest).error)
      }
    })

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })

    logger.dev('purgeOldSyncQueueEntries: Purge complete', { deletedCount })
  } catch (error) {
    logger.warn('purgeOldSyncQueueEntries: Error during transaction', { error })
  } finally {
    if (db) db.close()
  }
  return deletedCount
}

export const addDeletedObjects = async ({
  workspaceId,
  deletedItems,
}: {
  workspaceId: string
  deletedItems: { objectId: string; objectType: NativeObjectType }[]
}) => {
  if (!deletedItems || deletedItems.length === 0) {
    logger.dev('addDeletedObjects: No items provided.')
    return
  }
  logger.dev('addDeletedObjects: Starting to add deleted items', {
    workspaceId,
    count: deletedItems.length,
    sample: deletedItems.slice(0, 5),
  })
  let db: IDBDatabase | null = null
  let addedCount = 0
  const errors: { key: string; error: any }[] = []

  try {
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(
      [STORES.DELETED_OBJECTS, STORES.OBJECTS],
      'readwrite'
    )
    const deletedStore = tx.objectStore(STORES.DELETED_OBJECTS)
    const objectsStore = tx.objectStore(STORES.OBJECTS)

    const deletedAt = new Date().toISOString()

    const promises = deletedItems.map(({ objectId, objectType }) => {
      const key = buildObjectReference({ workspaceId, objectId, objectType })
      return new Promise<void>((resolve) => {
        const addRequest = deletedStore.put({ key, workspaceId, deletedAt })
        addRequest.onsuccess = () => {
          addedCount++
          const deleteRequest = objectsStore.delete(key)
          deleteRequest.onsuccess = () => {
            logger.dev('addDeletedObjects: Removed from main OBJECTS store', {
              key,
            })
            resolve()
          }
          deleteRequest.onerror = () => {
            logger.warn(
              'addDeletedObjects: Failed to remove from OBJECTS store',
              {
                key,
                errorName: deleteRequest.error?.name,
                errorMessage: deleteRequest.error?.message,
              }
            )
            resolve()
          }
        }
        addRequest.onerror = () => {
          logger.warn('addDeletedObjects: Failed to add entry', {
            key,
            errorName: addRequest.error?.name,
            errorMessage: addRequest.error?.message,
          })
          errors.push({
            key,
            error: `Name: ${addRequest.error?.name}, Message: ${addRequest.error?.message}`,
          })
          resolve()
        }
      })
    })

    await Promise.all(promises)

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => {
        logger.dev('addDeletedObjects: Transaction completed', {
          addedCount,
          errorCount: errors.length,
        })
        resolve()
      }
      tx.onerror = () => {
        logger.warn('addDeletedObjects: Transaction error', {
          errorName: tx.error?.name,
          errorMessage: tx.error?.message,
        })
        reject(tx.error)
      }
      tx.onabort = () => {
        logger.warn('addDeletedObjects: Transaction aborted', {
          errorName: tx.error?.name,
          errorMessage: tx.error?.message,
        })
        reject(tx.error)
      }
    })
  } catch (error) {
    logger.warn(
      'addDeletedObjects: Error during DB init or transaction setup',
      {
        error,
      }
    )
  } finally {
    if (db) db.close()
  }
  logger.dev('addDeletedObjects: Finished', {
    addedCount,
    errorCount: errors.length,
  })
}

export const updateProcessedUntilTimestamp = async (
  workspaceId: string,
  objectType: NativeObjectType,
  timestamp: string
): Promise<void> => {
  let db: IDBDatabase | null = null
  const key = `${workspaceId}-${objectType}`
  logger.dev('updateProcessedUntilTimestamp: Updating', {
    workspaceId,
    objectType,
    timestamp,
    key,
  })
  try {
    db = await initDb()
    const tx = (db as IDBDatabase).transaction(
      STORES.OBJECT_TYPE_METADATA,
      'readwrite'
    )
    const store = tx.objectStore(STORES.OBJECT_TYPE_METADATA)

    const entry = {
      key,
      workspaceId,
      objectType,
      processedUntilTimestamp: timestamp,
    }

    await new Promise<void>((resolve, reject) => {
      const request = store.put(entry)
      request.onsuccess = () => {
        logger.dev(
          'updateProcessedUntilTimestamp: Successfully updated/inserted',
          { key }
        )
        resolve()
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  } catch (error) {
    logger.warn('updateProcessedUntilTimestamp: Error during DB operation', {
      objectType,
      error,
    })
    throw error
  } finally {
    if (db) db.close()
  }
}
